import React, { useEffect, useState, useCallback } from 'react';
import {
  Users,
  RefreshCw,
  Phone,
  Building2,
  Tag,
  MessageSquare,
  Clock,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Search,
  Filter,
  Loader2,
} from 'lucide-react';

// ── Tipos ────────────────────────────────────────────────────────────────────
interface Lead {
  id: string;
  customerPhone: string;
  name: string | null;
  company: string | null;
  segment: string | null;
  mainNeed: string | null;
  interestedIn: string;
  status: string;
  createdAt: string;
}

// ── Configuração da API ───────────────────────────────────────────────────────
const API_BASE = (process.env.REACT_APP_API_URL ?? '').replace(/\/$/, '');

async function fetchLeads(): Promise<Lead[]> {
  const res = await fetch(`${API_BASE}/api/leads`);
  if (!res.ok) throw new Error('Erro ao carregar leads');
  const json = await res.json();
  return json.data as Lead[];
}

async function updateLeadStatus(id: string, status: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/leads/${id}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  if (!res.ok) throw new Error('Erro ao atualizar status');
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  new: { label: 'Novo', color: 'bg-blue-500/20 text-blue-300 border-blue-500/30', icon: <AlertCircle size={12} /> },
  aguardando_atendimento: { label: 'Aguardando', color: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30', icon: <Clock size={12} /> },
  contacted: { label: 'Contactado', color: 'bg-purple-500/20 text-purple-300 border-purple-500/30', icon: <MessageSquare size={12} /> },
  converted: { label: 'Convertido', color: 'bg-green-500/20 text-green-300 border-green-500/30', icon: <CheckCircle2 size={12} /> },
  descartado: { label: 'Descartado', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: <XCircle size={12} /> },
};

const INTEREST_LABELS: Record<string, string> = {
  whatsapp: '📲 WhatsApp',
  sistema: '💻 Sistema',
  agendamento: '🗓️ Agendamento',
  especialista: '🤝 Especialista',
  demo: '🎬 Demo',
};

function getStatusCfg(status: string) {
  return STATUS_CONFIG[status] ?? { label: status, color: 'bg-white/10 text-white/40 border-white/10', icon: null };
}

function formatDate(iso: string) {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(new Date(iso));
}

// ── Componente principal ──────────────────────────────────────────────────────
export default function AdminLeadsPage() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterInterest, setFilterInterest] = useState('all');
  const [updating, setUpdating] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchLeads();
      setLeads(data);
    } catch (e: any) {
      setError(e.message ?? 'Erro desconhecido');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (id: string, status: string) => {
    setUpdating(id);
    try {
      await updateLeadStatus(id, status);
      setLeads((prev) => prev.map((l) => l.id === id ? { ...l, status } : l));
    } catch {
      alert('Erro ao atualizar status. Tente novamente.');
    } finally {
      setUpdating(null);
    }
  };

  // Filtros
  const filtered = leads.filter((l) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      (l.name ?? '').toLowerCase().includes(q) ||
      (l.company ?? '').toLowerCase().includes(q) ||
      l.customerPhone.includes(q) ||
      (l.segment ?? '').toLowerCase().includes(q);
    const matchStatus = filterStatus === 'all' || l.status === filterStatus;
    const matchInterest = filterInterest === 'all' || l.interestedIn === filterInterest;
    return matchSearch && matchStatus && matchInterest;
  });

  // Métricas
  const total = leads.length;
  const newCount = leads.filter((l) => l.status === 'new' || l.status === 'aguardando_atendimento').length;
  const convertedCount = leads.filter((l) => l.status === 'converted').length;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <Users size={20} className="text-purple-400" />
            Leads via WhatsApp
          </h1>
          <p className="text-sm text-white/40 mt-0.5">
            Contatos capturados nos fluxos /webhook/twilio e /webhook/whatsapp
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white/60 hover:text-white hover:bg-white/10 text-sm font-medium transition disabled:opacity-40"
        >
          {loading ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
          Atualizar
        </button>
      </div>

      {/* Métricas */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total de Leads', value: total, color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/20' },
          { label: 'Aguardando Atenção', value: newCount, color: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/20' },
          { label: 'Convertidos', value: convertedCount, color: 'text-green-400', bg: 'bg-green-500/10 border-green-500/20' },
        ].map(({ label, value, color, bg }) => (
          <div key={label} className={`rounded-2xl border p-4 ${bg}`}>
            <p className="text-xs text-white/40 uppercase tracking-wider font-medium">{label}</p>
            <p className={`text-3xl font-bold mt-1 ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input
            type="text"
            placeholder="Buscar por nome, empresa, telefone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-xl pl-8 pr-3 py-2 text-sm text-white placeholder-white/25 outline-none focus:border-purple-500/50 transition"
          />
        </div>

        <div className="flex items-center gap-2">
          <Filter size={14} className="text-white/30" />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-[#0a0820] border border-white/10 rounded-xl px-3 py-2 text-sm text-white/70 outline-none focus:border-purple-500/50 transition"
          >
            <option value="all">Todos os status</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>

          <select
            value={filterInterest}
            onChange={(e) => setFilterInterest(e.target.value)}
            className="bg-[#0a0820] border border-white/10 rounded-xl px-3 py-2 text-sm text-white/70 outline-none focus:border-purple-500/50 transition"
          >
            <option value="all">Todos os interesses</option>
            {Object.entries(INTEREST_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Erro */}
      {error && (
        <div className="rounded-xl bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-400">
          ⚠️ {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16 text-white/30">
          <Loader2 size={24} className="animate-spin mr-2" />
          Carregando leads...
        </div>
      )}

      {/* Sem resultados */}
      {!loading && !error && filtered.length === 0 && (
        <div className="text-center py-16 text-white/25">
          <Users size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">Nenhum lead encontrado.</p>
          {leads.length > 0 && <p className="text-xs mt-1">Tente ajustar os filtros.</p>}
        </div>
      )}

      {/* Tabela */}
      {!loading && filtered.length > 0 && (
        <div className="overflow-x-auto rounded-2xl border border-white/8">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-white/3 border-b border-white/8">
                {['Contato', 'Empresa / Segmento', 'Interesse', 'Necessidade', 'Status', 'Data'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-white/30 uppercase tracking-wider whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filtered.map((lead) => {
                const cfg = getStatusCfg(lead.status);
                const isUpdating = updating === lead.id;
                return (
                  <tr key={lead.id} className="hover:bg-white/3 transition group">
                    {/* Contato */}
                    <td className="px-4 py-3">
                      <div className="font-medium text-white">{lead.name ?? <span className="text-white/30 italic">Sem nome</span>}</div>
                      <div className="flex items-center gap-1 text-white/40 text-xs mt-0.5">
                        <Phone size={10} />
                        {lead.customerPhone}
                      </div>
                    </td>
                    {/* Empresa / Segmento */}
                    <td className="px-4 py-3">
                      {lead.company ? (
                        <div className="flex items-center gap-1 text-white/70">
                          <Building2 size={12} className="text-white/30 shrink-0" />
                          {lead.company}
                        </div>
                      ) : null}
                      {lead.segment ? (
                        <div className="flex items-center gap-1 text-white/40 text-xs mt-0.5">
                          <Tag size={10} />
                          {lead.segment}
                        </div>
                      ) : <span className="text-white/20 text-xs italic">—</span>}
                    </td>
                    {/* Interesse */}
                    <td className="px-4 py-3 whitespace-nowrap text-white/60">
                      {INTEREST_LABELS[lead.interestedIn] ?? lead.interestedIn}
                    </td>
                    {/* Necessidade */}
                    <td className="px-4 py-3 max-w-[200px]">
                      <p className="text-white/60 text-xs line-clamp-2">{lead.mainNeed ?? <span className="text-white/20 italic">—</span>}</p>
                    </td>
                    {/* Status */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg.color}`}>
                          {cfg.icon}
                          {cfg.label}
                        </span>
                        {isUpdating && <Loader2 size={12} className="animate-spin text-white/30" />}
                      </div>
                      <select
                        value={lead.status}
                        disabled={isUpdating}
                        onChange={(e) => handleStatusChange(lead.id, e.target.value)}
                        className="mt-1.5 bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-xs text-white/50 outline-none focus:border-purple-500/50 transition disabled:opacity-40 cursor-pointer hover:border-white/20"
                      >
                        {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                          <option key={k} value={k}>{v.label}</option>
                        ))}
                      </select>
                    </td>
                    {/* Data */}
                    <td className="px-4 py-3 whitespace-nowrap text-white/30 text-xs">
                      <div className="flex items-center gap-1">
                        <Clock size={10} />
                        {formatDate(lead.createdAt)}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Rodapé com contagem */}
      {!loading && filtered.length > 0 && (
        <p className="text-xs text-white/20 text-right">
          Exibindo {filtered.length} de {total} lead{total !== 1 ? 's' : ''}
        </p>
      )}
    </div>
  );
}
